<template>
  <div>
    <div
      id="app"
      :class="{
        light: useLightUi,
        'extra-light': isLogin || isWithSrpRefreshOnMarketplace,
        'white-bg': showWhiteBg,
        'showing-banner': showBanner,
      }"
    >
      <div
        class="all-headers-wrapper"
        :class="{
          light: useLightUi,
          'mobile-height': isTabletExperience || isPhoneExperience,
          'white-bg': showWhiteBg || isPasswordPage,
        }"
        isTabletExperience
      >
        <acv-banner
          v-if="showBanner"
          id="issues-banner"
          :class="{
            'mobile-height': isTabletExperience || isPhoneExperience,
          }"
          variant="error"
          button-content="How to Resolve"
          class="tw-text-text-secondary"
          title="How To Resolve"
          @banner-button-click="showIssues()"
        >
          <strong>Your access has changed due to your AuctionACCESS account:</strong>
          {{ auctionAccessBannerMsg }}
        </acv-banner>
        <component
          :is="header"
          v-if="showHeader && !fromInitialization"
          id="acvHeader"
          :back-button-route="computedBackButtonRoute"
          :is-authorized="hasAccess"
          :current-user="currentUser"
          :dealership-name="dealershipName"
          :has-outstanding-auctions="hasOutstandingAuctions"
          :has-access="hasAccess"
          :show-filter-icon="showFilterIcon"
          :show-back-button="showBackButton"
          :can-sell="canSell"
          :number-of-active-filters="numberOfActiveFilters"
          :show-buttons="showRegistrationHeaderButtons"
          :aa-error-banner="showBanner"
        />
      </div>

      <div
        v-if="isLogin"
        class="half-white tw-bg-white"
      />
      <acv-visibility-change @visibility-changed="handleVisibilityChange" />

      <router-view
        :class="{
          'default-view': showHeader && !isOnMarketplace && !isLogin,
          'authentication-view': isLogin || isAuctionAccessPage,
          'simplified-view': isAuthorized && simplifiedHeader,
          'mobile-view__tablet': isAuthorized && isTabletExperience,
          'mobile-view__desktop': isAuthorized && !isTabletExperience
            && !isPhoneExperience,
          'aa-error-banner-padding': showBanner,
        }"
        :aa-error-banner="showBanner"
      />
      <acv-footer
        v-if="showFooter"
        id="acvFooter"
        :is-authorized="isAuthorized"
      />

      <acv-authentication-footer
        v-if="showAuthenticationFooter"
        :is-login="isLogin"
      />

      <component
        :is="bottomNavigationComponent"
        id="acvMobileNav"
        :route-name="routeName"
      />

      <acv-toast-message-list
        v-model="toastMessages"
        class="toast-message-list"
        @toastMessageLinkedClicked="handleToastMessageLinkClicked"
      />

      <acv-unsupported-browser-alert
        v-if="unsupportedBrowserDetected"
        :show="unsupportedBrowserDetected && !unsupportedBrowserMessageDismissed"
        @dismissed="unsupportedBrowserMessageDismissed = true"
      />
      <acv-offline-alert
        v-if="showOfflineAlert"
      />
      <acv-dev-tools-panel
        v-if="includeDevToolsPanel"
      />
      <acv-app-launch-message
        v-if="showAppLaunchMessage"
        :app-launch-message="appLaunchMessage"
        @hide="clearAppLaunchMessage"
      />
      <acv-auction-common-modals
        v-if="allowAuctionCommonModals"
      />
      <acv-terms-of-service-modal
        v-if="tosEnabled"
        id="terms-of-service"
      />
      <!-- eslint-disable vue/no-v-html -->
      <div
        v-if="tosEnabled && tosPrintContent != null"
        id="terms-of-service-print-view"
        v-html="tosPrintContent"
      />
    </div>
    <auction-access-dialog
      :ref="`auctionAccessIssueDialog-${auctionAccessId}`"
      :dealer-issues="aaDealerIssues"
      :auction-access-id="auctionAccessId"
      :show-a-a-dialog-default="false"
      :a-a-dialog-title="aaDialogTitle"
      a-a-dialog-submit-text="Close"
      class="tw-absolute"
    />
  </div>
</template>

<script>
import get from 'lodash.get';
import { mapGetters, mapState, mapActions } from 'vuex';
import { vueWindowSizeMixin } from 'vue-window-size';
import { AcvBanner, AcvVisibilityChange, AcvToastMessageList } from '@acv/shared-vuejs';
import AcvFooter from '@/components/AcvFooter.vue';
import AcvHeader from '@/components/Header.vue';
import AcvSimplifiedHeader from '@/components/SimplifiedHeader.vue';
import AcvSearchHeader from '@/components/Search/SearchHeader.vue';
import Analytics from '@/mixins/Analytics';
import BrowserDetection from '@/mixins/BrowserDetection';
import DevFlags from '@/mixins/DevFlags';
import FeatureFlags from '@/mixins/FeatureFlags';
import RegisterServiceWorker from '@/mixins/RegisterServiceWorker';
import AcvOfflineAlert from '@/components/OfflineAlert.vue';
import { START_IDLE_TIMER, CLEAR_IDLE_TIMER } from '@/store/modules/network/network.type';
import Logout from '@/mixins/Logout';
import MobileTopBarHelpers from '@/mixins/MobileTopBarHelpers';
import {
  isLocked,
} from '@/utilities/JwtRefreshTokensLock';
import { datadogRum } from '@datadog/browser-rum';
import AcvTermsOfServiceModal from '@/components/TermsOfService/TermsOfServiceModal.vue';

export default {
  name: 'App',
  components: {
    AcvBanner,
    AcvDevToolsPanel: () => import('@/components/DevToolsPanel.vue'),
    AcvSimplifiedHeader,
    AcvFooter,
    AcvHeader,
    AuctionAccessDialog: () => import('@/components/AuctionAccess/AuctionAccessDialog.vue'),
    AcvSearchHeader,
    AcvTermsOfServiceModal,
    AcvAuthenticationHeader: () => import('@/components/AcvAuthenticationHeader.vue'),
    AcvAuthenticationFooter: () => import('@/components/AcvAuthenticationFooter.vue'),
    AcvSearchBottomNavigation: () => import('@/components/Search/SearchBottomNavigation.vue'),
    AcvToastMessageList,
    AcvUnsupportedBrowserAlert: () => import('@/components/UnsupportedBrowserAlert.vue'),
    AcvOfflineAlert,
    AcvAppLaunchMessage: () => import('@/components/Modals/AppLaunchMessage.vue'),
    AcvMobileTopBar: () => import('@/components/MobileTopBar.vue'),
    AcvVisibilityChange,
    AcvAuctionCommonModals: () => import('@/components/Auction/AuctionCommonModals.vue'),
    AcvRegistrationHeader: () => import('@/components/AuctionAccess/AcvRegistrationHeader.vue'),
  },
  mixins: [
    Analytics,
    BrowserDetection,
    DevFlags,
    FeatureFlags,
    Logout,
    MobileTopBarHelpers,
    RegisterServiceWorker,
    vueWindowSizeMixin,
  ],
  beforeRouteLeave() {
    if (this.mobileMenuOpened) {
      document.body.classList.remove('no-scroll');
    }
  },
  data: () => ({
    unsupportedBrowserMessageDismissed: false,
    toastMessages: [],
    inactiveDealerWarningId: 'inactiveDealerWarningId',
    dealerActivatedMessageId: 'dealerActivatedMessageId',
    troublePullingTermsId: 'troublePullingTermsId',
    termsOfServiceAcceptedId: 'termsOfServiceAcceptedId',
    loggedInCheckInterval: null,
    breadcrumbBackRoute: '',
    launchMessageInterval: null,
    canShowLaunchMessage: true,
    auctionAccessBannerMsg: '',
  }),
  computed: {
    ...mapGetters({
      isAuthorized: 'isAuthorized',
      currentUser: 'currentUser',
      isLimitedUser: 'isLimitedUser',
      clockOffset: 'auctions/clockOffset',
      hasOutstandingAuctions: 'auctions/hasOutstandingAuctions',
      isPubnubOnline: 'isPubnubOnline',
      activeFilters: 'filters/activeFilters',
      mobileMenuOpened: 'mobileTopBar/mobileMenuOpened',
      newTosAcceptanceRequired: 'newTosAcceptanceRequired',
      newTosContentUrl: 'newTosContentUrl',
      getTosErrors: 'getTosErrors',
      getTermsOfServiceAccepted: 'getTermsOfServiceAccepted',
      getTosSuccessToastShown: 'getTosSuccessToastShown',
      currentTosCheckInProgress: 'currentTosCheckInProgress',
      srpRefreshPreference: 'marketplaceView/srpRefreshPreference',
      aaDealerIssues: 'aaDealerIssues',
      auctionAccessId: 'auctionAccessId',
    }),
    ...mapState({
      appLaunchMessage: (state) => state.notifications.appLaunchMessage,
      currentTourPath: (state) => state.tours.currentTourPath,
      tosPrintContent: (state) => state.termsOfService.tosPrintContent,
    }),
    showWhiteBg() {
      const isMarketReportOrSingleAuction = this.$route.path === '/market-report'
        || this.$route.path.includes('/auction');
      if (isMarketReportOrSingleAuction) return false;
      return (this.isPasswordPage || this.isAuthorized) && !this.isLogin;
    },
    header() {
      if (this.isLogout) {
        return undefined;
      }

      if (this.isLogin || this.isPasswordPage) {
        return 'AcvAuthenticationHeader';
      }

      if (this.isAuctionAccessPage) {
        return 'AcvRegistrationHeader';
      }

      if (this.isAuthorized) {
        // show top bar on phone and tablet if route allows for it or mobile menu open
        if (this.isMobileExperience
          && (this.$route.meta.hasMobileTopBarHeader || this.mobileMenuOpened)
        ) {
          return this.hasMobileTopBarContent
            ? 'AcvMobileTopBar'
            : undefined;
        }

        // Digital Clerks Titles Page
        if (this.simplifiedHeader) {
          return 'AcvSimplifiedHeader';
        }

        // no header on tablet
        if (this.isTabletExperience && !this.useLightUi) {
          return undefined;
        }

        // show search header only when not on a phone
        if (!this.isPhoneExperience) {
          return 'AcvSearchHeader';
        }
      }

      // show legacy header by default
      return 'AcvHeader';
    },
    bottomNavigationComponent() {
      if (!this.isLogout
        && (this.isMobileSearchExperience
        || (this.isMobileExperience && this.showMobileViewNavigation))
      ) {
        return 'AcvSearchBottomNavigation';
      }

      return undefined;
    },
    showBanner() {
      return this.featureFlags[this.featureFlagSlugs.displayAAIssuesAfterLogin]
        && this.auctionAccessBannerMsg.length > 0
        && this.isAuthorized
        && !this.mobileMenuOpened;
    },
    useLightUi() {
      return this.$route.meta.useLightUi;
    },
    simplifiedHeader() {
      return this.$route.meta.simplifiedHeader;
    },
    isOnMarketplace() {
      return ['search', 'marketplace'].includes(this.routeName.toLowerCase());
    },
    isWithSrpRefreshOnMarketplace() {
      return this.routeName.toLowerCase() === 'marketplace'
        && this.featureFlags[this.featureFlagSlugs.enableSrpRefresh]
        && this.srpRefreshPreference;
    },
    isPhoneExperience() {
      return this.windowWidth < this.$tailwindHelper.getCurrentBreakpointByKey('tw-md').value;
    },
    isTabletExperience() {
      return this.windowWidth >= this.$tailwindHelper.getCurrentBreakpointByKey('tw-md').value
        && this.windowWidth < this.$tailwindHelper.getCurrentBreakpointByKey('tw-lg').value;
    },
    isMobileExperience() {
      return this.isTabletExperience || this.isPhoneExperience;
    },
    isMobileSearchExperience() {
      return this.isOnMarketplace && this.isMobileExperience;
    },
    isLogin() {
      return this.$route.path.includes('/login');
    },
    isPasswordPage() {
      const routePath = this.$route.path;
      return routePath.includes('/forgot') || routePath.includes('/password');
    },
    isAuctionAccessPage() {
      const routePath = this.$route.path;
      return routePath.includes('/registration');
    },
    isLogout() {
      return this.$route.path.includes('/logout');
    },
    routeName() {
      return get(this, '$route.name', '') || '';
    },
    numberOfActiveFilters() {
      return this.activeFilters.length;
    },
    includeDevToolsPanel() {
      return import.meta.env.VITE_ENV_NAME === 'development'
        || this.featureFlags[this.featureFlagSlugs.enableDevtoolsWeb];
    },
    enableStockwaveIntegration() {
      return this.featureFlags[this.featureFlagSlugs.enableStockwaveIntegration];
    },
    showAppLaunchMessage() {
      return this.appLaunchMessage
        && this.canShowLaunchMessage
        && this.hasAccess
        && !this.currentTourPath
        && !this.fromInitialization;
    },
    showAuthenticationFooter() {
      const unauthorizedAndNotRegistering = !this.isAuthorized && !this.fromInitialization;
      const isAuthorizedAndRegistered = this.isAuthorized && !this.useLightUi
        && !this.fromInitialization;
      /* eslint-disable no-mixed-operators */
      return ((unauthorizedAndNotRegistering) || (isAuthorizedAndRegistered)
        && (this.isLogin)) && !this.isSsoPage;
    },
    computedBackButtonRoute() {
      if (this.$route.path.includes('/myacv/')) {
        return '/myacv';
      }
      return get(this, 'breadcrumbBackRoute', '/auctions/active');
    },
    aaDialogTitle() {
      if (this.aaDealerIssues !== undefined) {
        return `AuctionACCESS Issue${this.aaDealerIssues?.length > 1 ? 's' : ''}`;
      }
      return '';
    },
    fromInitialization() {
      return get(this, '$route.path') === '/';
    },
    canSell() {
      return this.$store.getters.canSell;
    },
    showBackButton() {
      return this.$route.meta.showBackButton;
    },
    hasAccess() {
      return this.isAuthorized
        && !this.$route.path.includes('/registration');
    },
    isSsoPage() {
      return this.$route?.meta?.ssoRoute || false;
    },
    showFilterIcon() {
      return this.$route.meta.showFilterIcon
      || (this.$route.name === 'BuyingRunList');
    },
    showHeader() {
      if (window.acvNative) {
        return false;
      }
      return get(this, '$route.meta.showHeader', true);
    },
    showFooter() {
      if (window.acvNative) {
        return false;
      }
      const showFooter = get(this, '$route.meta.showFooter', true);
      return showFooter
        && !this.fromInitialization
        && !this.useLightUi;
    },
    showRegistrationHeaderButtons() {
      return get(this, '$route.meta.showRegistrationHeaderButtons', true);
    },
    showMobileViewNavigation() {
      return (this.showFooter || this.hideFooterOverride)
        && !this.fromInitialization
        && this.hasAccess
        && !this.hasOutstandingAuctions;
    },
    showOfflineAlert() {
      if (!this.isPubnubOnline) {
        this.track(this.eventNames.noNetworkDialogShown);
      }
      return !this.isPubnubOnline;
    },
    dealershipName() {
      if (this.currentUser.hasMultipleDealerships) {
        return 'Multi-Dealer Account';
      }

      return this.currentUser.dealershipName;
    },
    allowAuctionCommonModals() {
      return this.$route.meta.allowAuctionCommonModals;
    },
    clockDrifted() {
      return (this.clockOffset > 5
          && this.featureFlags[this.featureFlagSlugs.enableClockDriftTracking]
      );
    },
    tosEnabled() {
      if (!this.isLogout && this.currentUser.user_id !== undefined) {
        return this.featureFlags[this.featureFlagSlugs.enableTosAcceptance]
        && this.hasAccess
        && this.newTosAcceptanceRequired
        && !!this.newTosContentUrl
        && !this.getTermsOfServiceAccepted;
      }
      return false;
    },
  },
  watch: {
    aaDealerIssues: {
      handler(newValue) {
        if (newValue !== undefined && newValue?.length > 0) {
          let msgString = 'There is an issue with your AuctionACCESS Account';
          const numIssues = newValue?.length;
          if (numIssues > 1) {
            msgString = `${newValue[0].message}, and ${numIssues - 1} other issue${numIssues - 1 > 1 ? 's' : ''}.`;
          } else if (numIssues === 1) {
            msgString = `${newValue[0].message}.`;
          }
          this.auctionAccessBannerMsg = msgString;
        } else {
          this.auctionAccessBannerMsg = '';
        }
      },
      deep: true,
      immediate: true,
    },
    async isAuthorized() {
      if (this.isAuthorized) {
        this.setupCustomAnalyticsAttributes();
        this.track(this.eventNames.opened, {
          cold_start: true,
          os_level_notification_enabled: get(window, 'Notification.permission') === 'granted',
        });
        this.initializeApplication();
      }

      if (!this.isAuthorized) {
        await this.$store.dispatch('disableMessaging');
      }
    },
    getTosErrors: {
      handler(newValue, oldValue) {
        if (newValue !== undefined
          && oldValue !== undefined
          && newValue.length !== oldValue.length
          && newValue.length > 0
        ) {
          this.addTroublePullingTermsMessage();
        }
      },
      deep: true,
      immediate: true,
    },
    getTermsOfServiceAccepted: {
      handler(newValue, oldValue) {
        if (newValue !== oldValue
          && newValue === true
          && !this.getTosSuccessToastShown) {
          this.addTermsOfServiceAcceptedSuccessfullyMessage();
          this.setTosSuccessToastShown(true);
        }
      },
      immediate: true,
    },
    isLimitedUser(isLimitedUser, wasIsLimitedUser) {
      this.clearToastMessageById(this.dealerActivatedMessageId);
      if (this.isLimitedUser && this.hasAccess) {
        this.addInactiveDealerWarning();
      } else if (!this.isLimitedUser) {
        this.clearToastMessageById(this.inactiveDealerWarningId);
        if (isLimitedUser === false
          && wasIsLimitedUser === true
          && this.isAuthorized) {
          this.addDealerActivatedMessage();
        }
      }
    },
    hasOutstandingAuctions(newValue) {
      if (newValue) {
        this.$router.push('/auctions/outstanding');
      }
    },
    '$route.name': {
      handler(newName, oldName) {
        // on route change, if there was any lingering app launch message,
        // show them else remain hiding them
        if (newName && oldName && !['login', 'logout'].includes(oldName)) {
          if (this.appLaunchMessage) {
            this.handleAppLaunchMessageVisibility(true);
          } else {
            this.handleAppLaunchMessageVisibility(false);
          }
        }

        // remove mobile top bar when route changes
        if (newName !== oldName) {
          this.teardownMobileTopBar();
        }
      },
    },
    mobileMenuOpened(newVal) {
      if (this.isMobileExperience) {
        if (newVal) {
          document.body.classList.add('no-scroll');
        } else {
          document.body.classList.remove('no-scroll');
        }
      }
    },
    isMobileExperience(newValue, oldValue) {
      if (!newValue && oldValue
        && document.body.classList.contains('no-scroll')
      ) {
        document.body.classList.remove('no-scroll');
      }
    },
    enableStockwaveIntegration: {
      immediate: true,
      async handler(newVal, oldVal) {
        if (newVal) {
          let stockwaveDevMode = null;
          let iFrameParentDocumentUrl = null;
          let isInIframe = null;
          try {
            /*
             * Inject stockwave script when the feature flag is enabled, the url domain
             * is an external or the stockwave debug flag is truthy
             */
            stockwaveDevMode = window?.localStorage
              ? window.localStorage.getItem('stockwave-dev-mode')
              : false;
            /*
             * trying this approach:
             * https://stackoverflow.com/a/7739035 - ETC
             */
            iFrameParentDocumentUrl = (window?.location !== window?.parent?.location)
              ? document?.referrer
              : document?.location?.href;
            isInIframe = window?.location !== window?.parent?.location;
            if (this.enableStockwaveIntegration
              && ((isInIframe
                && (iFrameParentDocumentUrl.includes('vauto.com')
                  || iFrameParentDocumentUrl.includes('app.coxautoinc.com')))
                || stockwaveDevMode)
            ) {
              // Need to disable the linting so we can dynamically inject our integration code - ETC
              if (!['production'].includes(import.meta.env.VITE_ENV_NAME)) {
                await import('@/vendors/Stockwave/integration-test.cjs');
              } else {
                await import('@/vendors/Stockwave/integration.cjs');
              }
              this.$nextTick(() => {
                window.stockwave.initialize();
              });
            } else if (oldVal && this.isAuthorized) {
              window.location.reload();
            }
          } catch (e) {
            this.noticeError('STOCKWAVE ERROR - Failed to load Stockwave script', {
              iFrameParentDocumentUrl,
              isInIframe,
              stockwaveDevMode,
            });
          }
        }
      },
    },
    clockDrifted() {
      if (this.clockDrifted) {
        this.track(this.eventNames.clockDrift, {
          offset: this.clockOffset,
        });
      }
    },
  },
  async created() {
    if (this.isAuthorized) {
      await this.refreshTokens();
      this.initializeApplication();
    }

    this.$root.$on('acv::setBreadcrumbBackRoute', this.setBreadcrumbBackRoute);
    this.$root.$on('acv::showToastMessage', this.showToastMessage);
    this.$root.$on('acv::clearToastMessageById', this.clearToastMessageById);
  },
  beforeDestroy() {
    this.$store.dispatch('marketplaceRouter/resetMarketplaceParams');
    this.$store.dispatch('tours/setCurrentTourPath', null);
  },
  destroyed() {
    this.clearLaunchMessageInterval();
    localStorage.removeItem('stockwave-dev-mode');
  },
  methods: {
    ...mapActions([
      START_IDLE_TIMER,
      CLEAR_IDLE_TIMER,
    ]),
    ...mapActions({
      refreshJwtTokens: 'refreshJwtTokens',
      preloadCapitalOpenInvoices: 'capital/preloadOpenInvoices',
      setTosSuccessToastShown: 'setTosSuccessToastShown',
      checkIfNeedTosAcceptance: 'checkIfNeedTosAcceptance',
      clearTosInfo: 'clearTosInfo',
      getSrpRefreshPreference: 'marketplaceView/getSrpRefreshPreference',
    }),
    showIssues() {
      this.track(this.eventNames.auctionAccess.dealershipSelection.dealershipIssues, {
        dealerAuctionAccessId: this.auctionAccessId,
        issues: this.aaDealerIssues,
      });
      this.$refs[`auctionAccessIssueDialog-${this.auctionAccessId}`].handleShow();
    },
    async refreshTokens() {
      if (!isLocked()) {
        try {
          await this.refreshJwtTokens();
        } catch (err) {
          // do nothing rn
        }
      }
    },
    async handleVisibilityChange(visible) {
      if (visible) {
        if (!window.acvNative) {
          await this.refreshTokens();
        }

        this[CLEAR_IDLE_TIMER]();
      } else {
        this[START_IDLE_TIMER]();
      }
    },
    addTermsOfServiceAcceptedSuccessfullyMessage() {
      this.showToastMessage({
        id: this.termsOfServiceAcceptedId,
        header: 'Terms of Service accepted successfully',
        severity: 'success',
        message: 'Please contact ACV at 1-800-552-4070 if you have any questions.',
        dismissible: true,
      });
      this.clearTosInfo();
    },
    addTroublePullingTermsMessage() {
      this.showToastMessage({
        id: this.troublePullingTermsId,
        header: 'Unable to process Terms of Service',
        severity: 'error',
        message: 'Please contact ACV at 1-800-552-4070 if this issue persists.',
        dismissible: true,
      });
    },
    addInactiveDealerWarning() {
      this.showToastMessage({
        id: this.inactiveDealerWarningId,
        header: this.currentUser.inactiveMessage.title,
        severity: 'warning',
        message: this.currentUser.inactiveMessage.text,
        dismissible: false,
      });
    },
    addDealerActivatedMessage() {
      this.showToastMessage({
        id: this.dealerActivatedMessageId,
        header: 'Dealer Activated',
        severity: 'success',
        message: 'Your dealer has been activated!',
        dismissible: true,
      });
    },
    async initializeApplication() {
      // if this is a mobile native view, do not initialize pubnub or firebase
      if (!window.acvNative) {
        await this.$store.dispatch('initMessaging');
        await this.$store.dispatch('initFirebase');
      }

      if (this.featureFlags[this.featureFlagSlugs.datadogSessionReplay]) {
        datadogRum.startSessionReplayRecording();
      }

      if (!this.isLimitedUser) {
        await this.$store.dispatch('auctions/checkForOutstandingAuctions');
      }

      this.$store.dispatch('funNotifications/startClearViewedFunNotificationsInterval');

      await this.getSrpRefreshPreference(); // get user preference for srp refresh
      this.$store.dispatch('checkUserIsLimited');
      await this.$store.dispatch('setIsCapitalUser');
      await this.$store.dispatch('checkEligibilityToRewardsProgram');
      await this.$store.dispatch('tours/getCompletedTours');

      await this.checkIfNeedTosAcceptance();

      if (!this.appLaunchMessage) {
        await this.fetchLaunchMessage();

        // if there is a result, show the app launch message else hide them for now
        if (this.appLaunchMessage) {
          this.handleAppLaunchMessageVisibility(true);
        } else {
          this.handleAppLaunchMessageVisibility(false);
        }
      }

      this.preInitCapital();
    },
    preInitCapital() {
      if (this.$route.path.includes('/capital/')) { return; }
      if (!this.currentUser.isCapitalUser) { return; }
      if (!this.currentUser.capitalUser?.dealer_selection_list) { return; }

      const [firstDealer] = this.currentUser.capitalUser.dealer_selection_list;
      let sessionDealer;
      if (window.sessionStorage) {
        sessionDealer = JSON.parse(window.sessionStorage.getItem('capital-portal'))?.dealer;
      }
      if (!firstDealer && !sessionDealer) return;

      // preload open invoices for capital users
      this.preloadCapitalOpenInvoices(sessionDealer?.id ?? firstDealer?.dealer_id);
    },
    async clearAppLaunchMessage() {
      // clear out the launch message and fetch any new messages
      // if there are no more messages, no longer show app launch messages
      await this.$store.dispatch('clearAppLaunchMessage');
      await this.fetchLaunchMessage();
      if (!this.appLaunchMessage) {
        this.handleAppLaunchMessageVisibility(false);
      }
    },
    async fetchLaunchMessage() {
      // fetch app launch messages and set a polling interval for every hour
      this.clearLaunchMessageInterval();

      if (!this.appLaunchMessage) {
        await this.$store.dispatch('getAppLaunchMessage');
      }

      this.launchMessageInterval = setInterval(() => {
        this.fetchLaunchMessage();
      }, 360000);
    },
    handleAppLaunchMessageVisibility(state) {
      // sets visibility of the app launch message and inversely if tours can be shown
      this.canShowLaunchMessage = state;
      this.$store.dispatch('tours/setCanShowTours', !state);
    },
    clearLaunchMessageInterval() {
      clearInterval(this.launchMessageInterval);
      this.launchMessageInterval = null;
    },
    clearError() {
      this.$store.dispatch('auctions/setError', '');
    },
    showToastMessage(newToastMessage) {
      const toastMessageId = newToastMessage.id;
      const toastMessageIndex = this.toastMessages
        .findIndex((toastMessage) => toastMessage.id === toastMessageId);
      if (toastMessageIndex > -1) { // prevent duplicate toast messages
        this.clearToastMessageById(toastMessageId);
        setTimeout(() => {
          this.toastMessages.push(newToastMessage);
        }, 250);
      } else {
        this.toastMessages.push(newToastMessage);
      }
    },
    setBreadcrumbBackRoute(breadcrumbRoute) {
      this.breadcrumbBackRoute = breadcrumbRoute;
    },
    clearToastMessageById(toastMessageId) {
      const toastMessageIndex = this.toastMessages
        .findIndex((toastMessage) => toastMessage.id === toastMessageId);
      if (toastMessageIndex > -1) {
        this.toastMessages.splice(toastMessageIndex, 1);
      }
    },
    handleToastMessageLinkClicked($event) {
      $event.$event.preventDefault();
      const { toastMessage: { id: toastMessageId } } = $event;
      if (toastMessageId === 'reloadToastMessage') {
        this.handleReloadToastMessageLinkClicked($event);
      }

      this.$root.$emit('acv::toastMessageLinkClicked', toastMessageId, $event.toastMessage);
    },
  },
};
</script>

<style lang="scss" scoped>
#app {
  background: #f2f2f2;
  min-height: 100vh;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .all-headers-wrapper {
    height: 75px;
    position: fixed;
    width: 100%;
    z-index: 5;

    &.white-bg {
      @apply tw-bg-white;
    }

    &.mobile-height {
      height: 55px;
    }

    .acv-banner {
      border-radius: 0;

      strong {
        font-weight: 500;
      }

      &.has-button {
        justify-content: space-between;
      }
    }

    #issues-banner {
      min-height: 100px;

      &.mobile-height {
        min-height: 170px;

        @screen tw-md {
          min-height: 147px;
        }
      }
    }
  }

  &.white-bg {
    @apply tw-bg-white;
  }

  &.showing-banner {
    :deep(.search-header) {
      top: 151px;

      @media tw-md {
        top: 135px;
      }
    }

    :deep(.acv-top-bar) {
      height: 3rem;
    }

    :deep(.acv-bottom-sheet__drawer--expanded) {
      bottom: -70px;
    }

    :deep(.auction-detail__header) {
      margin-top: 18px;
    }
  }

  .half-white {
    width: 100vw;
    height: 100vh;
    position: fixed;
    z-index: 0;

    @screen tw-lg {
      width: 51vw;
    }
  }

  &.extra-light {
    @apply tw-bg-background-light;
  }

  &.light {
    @apply tw-bg-white;
  }

  ::v-deep .acv-toast-message-list-container {
    z-index: 999999999;

    &.toast-message-list {
      bottom: 2.75rem;
      right: 0.5rem;

      @screen tw-md {
        right: 1rem;
      }

      @screen tw-lg {
        bottom: 2.5rem;
      }

      @screen tw-xl {
        right: 1.5rem;
      }
    }
  }

  .default-view:not(.simplified-view) {
    padding-top: 73px;
    min-height: calc(100vh - 73px);

    &.mobile-view {
      &__tablet {
        padding-top: 30px;
      }

      &__desktop {
        padding-top: 58px;

        &.aa-error-banner-padding {
          padding-top: 135px;

          :deep(.transport-header) {
            padding-top: 50px;
          }

          &.capital-portal {
            padding-top: 157px;
          }
        }
      }
    }

    &.offboarding-container {
      min-height: 100vh;
      padding-bottom: 5rem;
    }

    &.aa-error-banner-padding {
      padding-top: 240px;
      min-height: calc(100vh - 220px);

      &.capital-portal {
        padding-top: 255px;
      }

      &.mobile-view {
        &__tablet {
          padding-top: 140px;
        }
      }
    }
  }

  .authentication-view {
    padding-top: 73px;
    min-height: calc(100vh - 113px);

    @screen tw-md {
      min-height: calc(100vh - 4.4rem);
    }

    @media screen and (height <= 780px) {
      min-height: 575px;
    }
  }

  .simplified-view {
    padding-top: 58px;
    min-height: calc(100vh - 58px);
  }

  #acvFooter {
    z-index: 2;
  }

  #acvMobileNav {
    z-index: 6;
  }
}

#terms-of-service-print-view {
  display: none;
}

@media print {
  #app:has(#terms-of-service-print-view) {
    * {
      display: none;
    }

    #terms-of-service-print-view {
      display: block;

      ::v-deep {
        .page-number {
          display: none;
        }

        h1 {
          font-size: 3em;
        }
      }
    }
  }
}
</style>

<style lang="scss">
@use "@/scss/acv";
</style>
